import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Receipt, Send, Wallet, X, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { Card } from './ui/card';

const fmtINR = (n) => `₹${Math.abs(Number(n || 0)).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

/**
 * PairLedgerSheet — iter222 pair-wise ledger drill-down.
 *
 * Used from two places:
 *  - BalancesTab settlement suggestion (expands the summary into detail)
 *  - Members sub-tab "↔ with me" icon on each other-member card
 *
 * Props:
 *  - open, onOpenChange: modal control
 *  - groupId, otherUserId, otherName: required
 */
export default function PairLedgerSheet({ open, onOpenChange, groupId, otherUserId, otherName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !groupId || !otherUserId) return;
    setLoading(true);
    setData(null);
    api.get(`/groups/${groupId}/pair-ledger/${otherUserId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, groupId, otherUserId]);

  const net = data?.net_i_owe_them ?? 0;
  const iOwe = net > 0.005;
  const theyOwe = net < -0.005;
  const settled = !iOwe && !theyOwe;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="pair-ledger-sheet">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <span>You</span>
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <span>{otherName}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Net-owed hero */}
        {loading && <Skeleton className="h-24 w-full" />}
        {!loading && data && !data.error && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`rounded-xl p-4 border-2 ${
              iOwe ? 'bg-rose-500/5 border-rose-500/30' :
              theyOwe ? 'bg-emerald-500/5 border-emerald-500/30' :
              'bg-muted border-border'
            }`}
            data-testid="pair-ledger-hero"
          >
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
              {iOwe ? `You owe ${otherName}` : theyOwe ? `${otherName} owes you` : 'All settled up'}
            </div>
            <div className={`font-display text-3xl font-bold tabular-nums mt-1 ${
              iOwe ? 'text-rose-700' : theyOwe ? 'text-emerald-700' : ''
            }`} data-testid="pair-ledger-net">
              {settled ? '₹0' : fmtINR(net)}
            </div>
            {data.totals && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <StatMini label={`You paid for ${otherName}`} value={fmtINR(data.totals.i_paid_for_them)} tone="emerald" />
                <StatMini label={`${otherName} paid for you`} value={fmtINR(data.totals.they_paid_for_me)} tone="rose" />
                <StatMini label={`You sent to ${otherName}`} value={fmtINR(data.totals.i_sent)} tone="rose" />
                <StatMini label={`${otherName} sent to you`} value={fmtINR(data.totals.they_sent)} tone="emerald" />
              </div>
            )}
          </motion.div>
        )}

        {/* Entries list */}
        <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-3">
          {loading && (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          )}
          {!loading && data?.error && (
            <div className="p-6 text-center text-sm text-rose-600">Failed to load ledger.</div>
          )}
          {!loading && data && !data.error && data.entries.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No shared expenses or settlements between you two yet in this group.
            </div>
          )}
          {!loading && data && !data.error && data.entries.length > 0 && (
            <div className="space-y-1.5">
              <AnimatePresence>
                {[...data.entries].reverse().map((e, idx) => (
                  <motion.div
                    key={`${e.kind}-${e.id}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx * 0.02, 0.3), duration: 0.2 }}
                  >
                    <PairEntryRow entry={e} otherName={otherName} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatMini({ label, value, tone }) {
  const toneMap = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    muted: 'text-muted-foreground',
  };
  return (
    <div className="rounded-md bg-background/50 px-2 py-1.5">
      <div className="text-xs text-muted-foreground truncate">{label}</div>
      <div className={`font-bold tabular-nums ${toneMap[tone] || ''}`}>{value}</div>
    </div>
  );
}

function PairEntryRow({ entry, otherName }) {
  const isExpense = entry.kind === 'expense';
  const iOwe = entry.delta > 0.005;
  const Icon = isExpense ? Receipt : (entry.via === 'wallet_settle' ? Wallet : Send);

  let title;
  if (isExpense) {
    if (entry.label === 'you_paid') {
      title = `You paid ${otherName === undefined ? '' : `for ${otherName}`}`;
    } else {
      title = `${otherName} paid for you`;
    }
  } else {
    title = entry.label === 'you_sent' ? `You sent to ${otherName}` : `${otherName} sent to you`;
  }

  return (
    <Card className="p-2.5 flex items-start gap-2.5" data-testid={`pair-entry-${entry.kind}-${entry.id}`}>
      <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${
        iOwe ? 'bg-rose-500/10 text-rose-600' : 'bg-emerald-500/10 text-emerald-600'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate">{title}</div>
        <div className="text-xs text-muted-foreground truncate">
          {entry.description}
          {isExpense && entry.amount !== entry.share && (
            <span className="ml-1 opacity-70">
              · full bill {fmtINR(entry.amount)}, {otherName || 'their'} share {fmtINR(entry.share)}
            </span>
          )}
          {entry.via === 'wallet_settle' && <span className="ml-1 opacity-70">· wallet</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(entry.created_at)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-bold tabular-nums text-sm ${iOwe ? 'text-rose-600' : 'text-emerald-600'}`}>
          {iOwe ? '+' : '−'}{fmtINR(entry.delta)}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {entry.running_net_i_owe > 0.005 ? `You owe ${fmtINR(entry.running_net_i_owe)}` :
           entry.running_net_i_owe < -0.005 ? `${otherName} owes ${fmtINR(entry.running_net_i_owe)}` :
           '₹0 (settled)'}
        </div>
      </div>
    </Card>
  );
}
